import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from 'components/Spinner'
import Message from 'components/Message'
import { MessageSquare } from 'lucide-react'
import PopupRTE from 'components/PopupRTE'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import PostAddPopup from 'components/popups/PostAddPopup'
import convertRTEImages, { modifyNote } from 'utils/convertRTEImages'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import Post from 'components/Post'
import { createPost, createPostImage, initializePosts } from 'slices/postSlice'


const ComunityPostsFeed = () => {
	const dispatch = useDispatch()

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin
	const userId = userInfo._id
	const groupId = userInfo.lastGroup

	const [openPopup, setOpenPopup] = useState(false)

	const {
		entities: postEntities,
		ids: postIds,
		isLoading: postsLoading,
		isInitialized: isCommunityPostsInitialized,
		error,
	} = useSelector(state => state.posts)
	const posts = postIds.map(id => postEntities[id])

	const { group, loading } = useSelector(state => {
		return {
			loading: state.groups.isLoading,
			success: state.groups.isInitialized,
			group: state.groups.entities[groupId],
		}
	})

	useEffect(() => {
		// Without a selected group there is nothing to fetch. Dispatching anyway
		// produced a request to /api/posts/groupposts/undefined, which 404s and
		// logs a console error on a screen that is behaving correctly.
		if (!groupId) return
		dispatch(initializePosts({ groupId }))
	}, [dispatch, groupId, isCommunityPostsInitialized])

	const addPost = async newNote => {
		setOpenPopup(false)

		const fileArray = convertRTEImages(newNote.content)
		const groupId = newNote.postGroup

		const uploadedImages = []

		for (const file of fileArray) {
			const uploadedImage = await uploadToCloudinary(file)
			uploadedImages.push(uploadedImage)
		}

		const modifiedNote = modifyNote(newNote.content, uploadedImages)

		await dispatch(
			createPost({
				user: userInfo._id,
				message: modifiedNote,
				postGroup: groupId,
			}),
		)

		for (const uploadedImage of uploadedImages) {
			await dispatch(
				createPostImage({
					userid: userId,
					uuid: uploadedImage.public_id,
					name: uploadedImage.original_filename,
					size: uploadedImage.bytes,
					url: uploadedImage.secure_url,
					source: 'cloudinary',
					type: uploadedImage.format,
				}),
			)
		}
		await dispatch(initializePosts({ groupId }))
	}

	const openInPopup = () => {
		setOpenPopup(true)
	}

	return (
		<div>
			<div className="grid grid-cols-12 gap-12">
				<div className="col-span-1"></div>
				<div className="col-span-10">
					{loading ? (
						<Spinner />
					) : error ? (
						<Message variant="danger">{error}</Message>
					) : group ? (
						<div className="feed__header">
							<h2>{group.groupName}</h2>
						</div>
					) : (
						// No group selected. A new account has no `lastGroup`, so
						// `groupId` is undefined and `entities[undefined]` is too —
						// dereferencing it here threw and unmounted the whole tree,
						// leaving the entire /community screen blank for every new
						// user (DT-037).
						<div className="feed__header">
							<h2 className="text-[22px] font-normal text-gray-500 m-0">
								No group selected
							</h2>
							<p className="text-sm text-gray-500 mt-1 mb-0">
								Choose a group from the list on the left to see its posts, or
								switch to <span className="font-medium">All</span> to find one
								to join.
							</p>
						</div>
					)}

					<div className="feed">
						<div className="feed_inputContainer" style={{ display: `flex`, gridGap: '.5rem' }}>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<Avatar><AvatarImage src={userInfo.image} /><AvatarFallback /></Avatar>
							</div>
							<div style={{ flex: '1' }}>
								<form className="feed_newInput" onClick={openInPopup}>
									{` start a post . . .`}
									{error && <Message variant="danger">{error}</Message>}
								</form>
							</div>
						</div>
						<div className="feed_Container">
							{postsLoading ? (
								<Spinner />
							) : error ? (
								<Message variant="danger">{error}</Message>
							) : (
								<div>
									{posts.map(post => (
										<Post key={post._id} post={post} />
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<PopupRTE title="" openPopup={openPopup} setOpenPopup={setOpenPopup} icon={<MessageSquare className="w-5 h-5" />} description="Create a new post">
				<PostAddPopup addNote={addPost} postGroup={groupId} />
			</PopupRTE>
		</div>
	)
}

export default ComunityPostsFeed
